import { DemoSymbol } from './priceAnchors';

export interface RateBand {
  lendLow: number;
  lendHigh: number;
  borrowLow: number;
  borrowHigh: number;
}

export interface AssetRateConfig {
  floating: RateBand;
  fixed: RateBand;
  tradeMedianUsd: number;
  tradeP95Usd: number;
  whaleUsd: number;
  volume24Low: number;
  volume24High: number;
  spreadBpsMin: number;
  spreadBpsMax: number;
  topOfBookSizePct: number;
}

// Bands are decimal APR (0.05 = 5%). Borrow always > lend.
export const RATE_CONFIG: Record<DemoSymbol, AssetRateConfig> = {
  USDT: {
    floating: { lendLow: 0.04, lendHigh: 0.10, borrowLow: 0.05, borrowHigh: 0.12 },
    fixed:    { lendLow: 0.045, lendHigh: 0.09, borrowLow: 0.05, borrowHigh: 0.10 },
    tradeMedianUsd: 25000, tradeP95Usd: 750000, whaleUsd: 6000000,
    volume24Low: 5_000_000, volume24High: 50_000_000,
    spreadBpsMin: 3, spreadBpsMax: 8, topOfBookSizePct: 0.25,
  },
  USDC: {
    floating: { lendLow: 0.04, lendHigh: 0.09, borrowLow: 0.05, borrowHigh: 0.11 },
    fixed:    { lendLow: 0.045, lendHigh: 0.085, borrowLow: 0.05, borrowHigh: 0.095 },
    tradeMedianUsd: 20000, tradeP95Usd: 600000, whaleUsd: 5000000,
    volume24Low: 4_000_000, volume24High: 40_000_000,
    spreadBpsMin: 3, spreadBpsMax: 8, topOfBookSizePct: 0.25,
  },
  DAI: {
    floating: { lendLow: 0.04, lendHigh: 0.09, borrowLow: 0.05, borrowHigh: 0.11 },
    fixed:    { lendLow: 0.045, lendHigh: 0.085, borrowLow: 0.05, borrowHigh: 0.095 },
    tradeMedianUsd: 15000, tradeP95Usd: 400000, whaleUsd: 3000000,
    volume24Low: 2_000_000, volume24High: 20_000_000,
    spreadBpsMin: 3, spreadBpsMax: 8, topOfBookSizePct: 0.25,
  },
  ETH: {
    floating: { lendLow: 0.015, lendHigh: 0.04, borrowLow: 0.02, borrowHigh: 0.05 },
    fixed:    { lendLow: 0.02, lendHigh: 0.045, borrowLow: 0.025, borrowHigh: 0.05 },
    tradeMedianUsd: 10000, tradeP95Usd: 350000, whaleUsd: 3500000,
    volume24Low: 3_000_000, volume24High: 30_000_000,
    spreadBpsMin: 5, spreadBpsMax: 15, topOfBookSizePct: 0.2,
  },
  WBTC: {
    floating: { lendLow: 0.005, lendHigh: 0.025, borrowLow: 0.01, borrowHigh: 0.035 },
    fixed:    { lendLow: 0.01, lendHigh: 0.03, borrowLow: 0.015, borrowHigh: 0.035 },
    tradeMedianUsd: 8000, tradeP95Usd: 250000, whaleUsd: 2500000,
    volume24Low: 1_500_000, volume24High: 15_000_000,
    spreadBpsMin: 5, spreadBpsMax: 15, topOfBookSizePct: 0.2,
  },
};

export function fixedCurveMultiplier(days: number): number {
  // Smooth mild upward term structure spanning 1D..4Q (~1-365d). Linear ramp
  // clamped at one year so whole-curve rates stay in a plausible shape:
  // ~+0% at 1D, ~+1.3% at 90D, ~+2.6% at 180D, ~+5.2% at 365D.
  const d = Math.max(0, Math.min(days, 365));
  return 1 + d * 0.000145;
}
